import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Fab,
  Popover,
  Grid,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  TextField,
  InputAdornment,
  Button,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import Chip from '@material-ui/core/Chip';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import { toast } from 'react-toastify';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';
import { pt } from 'date-fns/locale';
import { format } from 'date-fns';

import api from '../../../services/api';
import { headerAdd } from '../../../store/modules/header/actions';
import { boxesRequest } from '../../../store/modules/boxes/actions';
import { updateInstrumentsRequest, updateAssetsRequest, updateTagsRequest, clearDemoData } from '../../../store/modules/user/actions';
import { correctPricePtBrToEn, checkStopWinLoss, shouldShowStopModal, markStopModalShown } from '../../../utils/functions';
import { ApplicationState } from '../../../store';
import StopWinLossModal from '../../StopWinLossModal';
import ManageTradeTypesModal from '../../ManageTradeTypesModal';
import ManageAssetsModal from '../../ManageAssetsModal';
import ManageTagsModal from '../../ManageTagsModal';
import { useStyles } from './AddTradeStyles';

export default function AddTrade() {
  const classes = useStyles();
  const matches = useMediaQuery('(min-width:600px)');
  const dispatch = useDispatch();
  const userState = useSelector((state: ApplicationState) => state.user);
  const boxesState = useSelector((state: ApplicationState) => state.boxes);
  
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [showManageTradeTypesModal, setShowManageTradeTypesModal] = useState(false);
  const [showManageAssetsModal, setShowManageAssetsModal] = useState(false);
  const [showManageTagsModal, setShowManageTagsModal] = useState(false);
  const [checkStopAfterTrade, setCheckStopAfterTrade] = useState(false);
  const [stopModal, setStopModal] = useState<{
    open: boolean;
    type: 'win' | 'loss';
    stopValue: number;
    todayTotal: number;
  }>({
    open: false,
    type: 'win',
    stopValue: 0,
    todayTotal: 0,
  });

  // Check stop win/loss after boxes data refreshes from a new trade
  useEffect(() => {
    if (!checkStopAfterTrade) return;
    if (boxesState.loading) return; // wait for boxes to finish loading

    setCheckStopAfterTrade(false);

    if (boxesState.boxes && boxesState.boxes.ProfitAndLoss && boxesState.boxes.ProfitAndLoss.today && boxesState.boxes.ProfitAndLoss.today.total !== undefined) {
      const todayTotal = typeof boxesState.boxes.ProfitAndLoss.today.total === 'string'
        ? parseFloat(boxesState.boxes.ProfitAndLoss.today.total)
        : boxesState.boxes.ProfitAndLoss.today.total;
      const stopWin = userState.user.config.stopWin || 0;
      const stopLoss = userState.user.config.stopLoss || 0;

      const checkResult = checkStopWinLoss(todayTotal, stopWin, stopLoss);

      if (checkResult.type && shouldShowStopModal(checkResult.type)) {
        setStopModal({
          open: true,
          type: checkResult.type,
          stopValue: checkResult.type === 'win' ? stopWin : stopLoss,
          todayTotal,
        });
        setAnchorEl(null);
      }
    }
  }, [boxesState.boxes, boxesState.loading, checkStopAfterTrade]); // eslint-disable-line

  function handleSubmit() {
    if (investment === '') {
      setInvestmentError(true)
    } else {
      setInvestmentError(false)
    }
    if (result === '') {
      setResultError(true)
    } else {
      setResultError(false)
    }
    if (result === '' || (investment === '' && instrument !== 'Forex')) return

    if (!inputStartDate || isNaN(inputStartDate.getTime()) || !inputEndDate || isNaN(inputEndDate.getTime())) {
      toast.error('Data/hora inválida. Use o formato dd/MM/yyyy HH:mm');
      return;
    }

    if (inputEndDate.getTime() <= inputStartDate.getTime()) {
      toast.error('Data de fim deve ser depois da data de início');
      return;
    }

    setLoading(true);
      api.post('trade', {
        startDate,
        endDate,
        instrument,
        asset,
        investment: instrument === 'Forex' ? 0 : correctPricePtBrToEn(investment),
        result: correctPricePtBrToEn(result),
        notes,
        tags: selectedTags
      }).then(res => {
        if (userState.user.hasDemoData) {
          dispatch(clearDemoData());
        }
        dispatch(boxesRequest());
        const correctResult = result.replace(/,/g, ".") * 1
        dispatch(headerAdd(correctResult));

        setLoading(false);
        toast.success('Trade criado com sucesso');
        
        // Flag to check stop win/loss after boxes data refreshes
        setCheckStopAfterTrade(true);
      }).catch(err => {
        setLoading(false);
        toast.error(err.response.data.error);
      })
  }

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStopModalClose = () => {
    markStopModalShown(stopModal.type);
    setStopModal(prev => ({ ...prev, open: false }));
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  // Tabs

  // TabPanel - Commented out as CSV import is removed
  // interface TabPanelProps {
  //   children?: React.ReactNode;
  //   index: any;
  //   value: any;
  // }

  // function TabPanel(props: TabPanelProps) {
  //   const { children, value, index, ...other } = props;

  //   return (
  //     <Typography
  //       component="div"
  //       role="tabpanel"
  //       hidden={value !== index}
  //       id={`simple-tabpanel-${index}`}
  //       aria-labelledby={`simple-tab-${index}`}
  //       {...other}
  //     >
  //       {value === index && <Box p={3}>{children}</Box>}
  //     </Typography>
  //   );
  // }

  // Tabs - Commented out as CSV import is removed
  // function a11yProps(index: any) {
  //   return {
  //     id: `simple-tab-${index}`,
  //     'aria-controls': `simple-tabpanel-${index}`,
  //   };
  // }

  // const [value, setValue] = useState(0);

  // const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
  //   setValue(newValue);
  // };

  // End Tabs

  // SelectAsset

  const userAssets = userState.user.config.assets || [];
  const defaultAsset = userAssets.find(a => a.isDefault) || userAssets[0];
  const [asset, setAsset] = useState((defaultAsset && defaultAsset.name) || 'Ouro');

  useEffect(() => {
    const assets = userState.user.config.assets || [];
    if (assets.length > 0) {
      const currentAssetExists = assets.some(a => a.name === asset);
      if (!currentAssetExists) {
        const newDefault = assets.find(a => a.isDefault) || assets[0];
        if (newDefault) {
          setAsset(newDefault.name);
        }
      }
    }
  }, [userState.user.config.assets, asset]);

  const handleAssetChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setAsset(event.target.value as string);
  };

  // End SelectAsset

  // SelectInstrument

  const instruments = userState.user.config.instruments || [];
  const defaultInstrument = instruments.find(inst => inst.isDefault) || instruments[0];
  const [instrument, setInstrument] = useState((defaultInstrument && defaultInstrument.name) || 'Binary Options');

  // Update instrument when instruments list changes (e.g., after adding new instrument)
  useEffect(() => {
    const instruments = userState.user.config.instruments || [];
    if (instruments.length > 0) {
      // Check if current instrument still exists in the list
      const currentInstrumentExists = instruments.some(inst => inst.name === instrument);
      
      if (!currentInstrumentExists) {
        // Current instrument doesn't exist, switch to default
        const newDefaultInstrument = instruments.find(inst => inst.isDefault) || instruments[0];
        if (newDefaultInstrument) {
          setInstrument(newDefaultInstrument.name);
        }
      }
    }
  }, [userState.user.config.instruments, instrument]);

  const handleInstrumentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setInstrument(event.target.value as string);
  };

  // End SelectInstrument

  // DateAndTime

  const [startDate, setStartDate] = useState<string>(
    format(new Date(), "yyyy'-'MM'-'dd'T'HH':'mm':'ssxxx")
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(Date.now() + 60000), "yyyy'-'MM'-'dd'T'HH':'mm':'ssxxx")
  );

  const [inputStartDate, setInputStartDate] = useState<Date | null>(new Date());
  const [inputEndDate, setInputEndDate] = useState<Date | null>(new Date(Date.now() + 60000));

  const handleStartDateChange = (selectedDate: Date | null) => {
    setInputStartDate(selectedDate);
    if (selectedDate != null && !isNaN(selectedDate.getTime())) {
      setStartDate(format(selectedDate, "yyyy'-'MM'-'dd'T'HH':'mm':'ssxxx"));
      const newEnd = new Date(selectedDate.getTime() + 60000);
      setInputEndDate(newEnd);
      setEndDate(format(newEnd, "yyyy'-'MM'-'dd'T'HH':'mm':'ssxxx"));
    }
  };

  const handleEndDateChange = (selectedDate: Date | null) => {
    setInputEndDate(selectedDate);
    if (selectedDate != null && !isNaN(selectedDate.getTime())) {
      setEndDate(format(selectedDate, "yyyy'-'MM'-'dd'T'HH':'mm':'ssxxx"));
    }
  };

  // End DateAndTime

  // Investment and Result

  const [investment, setInvestment] = useState('');
  const [result, setResult] = useState<any>('');
  const [investmentError, setInvestmentError] = useState(false);
  const [resultError, setResultError] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);


  const handleInvestmentChange = (event: any) => {
    setInvestment(event.target.value);
  };

  const handleResultChange = (event: any) => {
    setResult(event.target.value);
  };

  function getResultColor(): string {
    if (result === '') return '#fff';
    var num = parseFloat(String(result).replace(',', '.'));
    if (isNaN(num) || num === 0) return '#fff';
    return num > 0 ? '#5BEC5B' : '#FF7A63';
  }

  // End Investment and Result

  const matchesToPopover = useMediaQuery('(min-width:850px)');

  return (
    <>
      <StopWinLossModal
        open={stopModal.open}
        onClose={handleStopModalClose}
        type={stopModal.type}
        stopValue={stopModal.stopValue}
        todayTotal={stopModal.todayTotal}
      />
      <Tooltip title="Adicionar trade" placement="right">
        <Fab onClick={handleClick} className={classes.add} color="primary">
          <AddIcon />
        </Fab>
      </Tooltip>
      <Grid xs={12} sm={6} md={6} lg={4} item>
        <Popover
          style={matchesToPopover ? { top: '-70px', left: '39px' } : { top: '-41px', left: '-17px' }}
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box
            className={classes.popoverBox}
            style={{
              width: matches ? '440px' : '320px',
              maxHeight: matches ? 'none' : '80vh',
            }}
          >
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={pt}>
              <div className={classes.dateRow}>
                <KeyboardDateTimePicker
                  style={{ width: '48%' }}
                  autoOk
                  variant="inline"
                  format="dd/MM/yyyy HH:mm"
                  margin="normal"
                  ampm={false}
                  id="start-datetime-picker"
                  label="Início"
                  value={inputStartDate}
                  onChange={handleStartDateChange}
                  invalidDateMessage=""
                  KeyboardButtonProps={{
                    'aria-label': 'change start date time',
                  }}
                />
                <KeyboardDateTimePicker
                  style={{ width: '48%' }}
                  autoOk
                  variant="inline"
                  format="dd/MM/yyyy HH:mm"
                  margin="normal"
                  ampm={false}
                  id="end-datetime-picker"
                  label="Fim"
                  value={inputEndDate}
                  onChange={handleEndDateChange}
                  invalidDateMessage=""
                  KeyboardButtonProps={{
                    'aria-label': 'change end date time',
                  }}
                />
              </div>
            </MuiPickersUtilsProvider>


            <div className={classes.typeAssetRow}>
              <Box display="flex" alignItems="flex-end">
                <FormControl style={{ minWidth: '160px' }}>
                  <InputLabel id="tradeType-select">Tipo</InputLabel>
                  <Select
                    labelId="tradeType-select"
                    id="demo-simple-select"
                    value={instrument}
                    onChange={handleInstrumentChange}
                  >
                    {(userState.user.config.instruments && userState.user.config.instruments.map((instrumentOption) => (
                      <MenuItem key={instrumentOption.name} value={instrumentOption.name}>
                        {instrumentOption.name}
                      </MenuItem>
                    ))) || []}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => setShowManageTradeTypesModal(true)}
                  className={classes.addTypeButton}
                  size="small"
                >
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box display="flex" alignItems="flex-end">
                <FormControl style={{ minWidth: '160px' }}>
                  <InputLabel id="asset-select">Ativo</InputLabel>
                  <Select
                    labelId="asset-select"
                    id="asset-select-field"
                    value={asset}
                    onChange={handleAssetChange}
                  >
                    {(userState.user.config.assets && userState.user.config.assets.map((assetOption) => (
                      <MenuItem key={assetOption.name} value={assetOption.name}>
                        {assetOption.name}
                      </MenuItem>
                    ))) || []}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => setShowManageAssetsModal(true)}
                  className={classes.addTypeButton}
                  size="small"
                >
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </Box>
            </div>


            <div className={classes.valuesRow}>
              {instrument !== 'Forex' && (
                <TextField
                  id="manual-investment-number"
                  label="Investimento"
                  error={investmentError}
                  placeholder="30,00"
                  autoComplete="off"
                  value={investment}
                  onChange={handleInvestmentChange}
                  style={{ width: '48%' }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">R$</InputAdornment>
                    ),
                  }}
                />
              )}
              <TextField
                id="manual-result-number"
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Resultado
                    <Tooltip title="Use valor negativo para prejuízo. Ex: -50" arrow placement="top">
                      <HelpOutlineIcon style={{ fontSize: '20px', color: '#ffffff88', cursor: 'help', verticalAlign: 'middle', marginBottom: '2px' }} />
                    </Tooltip>
                  </span>
                }
                error={resultError}
                autoComplete="off"
                placeholder="27,00"
                value={result}
                onChange={handleResultChange}
                style={{
                  width: instrument !== 'Forex' ? '48%' : '100%',
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  style: { color: getResultColor() },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><span style={{ color: getResultColor() }}>R$</span></InputAdornment>
                  ),
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Box display="flex" alignItems="flex-end" style={{ width: '50%' }}>
                <FormControl style={{ flex: 1 }}>
                  <InputLabel id="tags-select-label">Tags</InputLabel>
                  <Select
                    labelId="tags-select-label"
                    multiple
                    value={selectedTags}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) => setSelectedTags(e.target.value as string[])}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                    renderValue={(selected) => (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                        {(selected as string[]).map((tag) => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </div>
                    )}
                  >
                    {(userState.user.config.tags || []).map((tag: string) => (
                      <MenuItem key={tag} value={tag}>
                        <Checkbox checked={selectedTags.includes(tag)} size="small" color="primary" />
                        <ListItemText primary={tag} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => setShowManageTagsModal(true)}
                  className={classes.addTypeButton}
                  size="small"
                >
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                id="manual-notes"
                label="Notas"
                placeholder="Anotações..."
                autoComplete="off"
                multiline
                minRows={1}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ width: '50%' }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>

            <Button
              className={classes.submitButton}
              variant="contained"
              onClick={() => handleSubmit()}
              component="span"
              disabled={!!loading}
            >
              {loading ? <CircularProgress size={22} style={{ color: '#fff' }} /> : 'Adicionar'}
            </Button>
          </Box>
        </Popover>
        <ManageTradeTypesModal
          open={showManageTradeTypesModal}
          onClose={() => setShowManageTradeTypesModal(false)}
          tradeTypes={userState.user.config.instruments || []}
          onSave={(newInstruments: any[]) => {
            dispatch(updateInstrumentsRequest(newInstruments));
            setShowManageTradeTypesModal(false);
          }}
        />
        <ManageAssetsModal
          open={showManageAssetsModal}
          onClose={() => setShowManageAssetsModal(false)}
          assets={userState.user.config.assets || []}
          onSave={(newAssets: any[]) => {
            dispatch(updateAssetsRequest(newAssets));
            setShowManageAssetsModal(false);
          }}
        />
        <ManageTagsModal
          open={showManageTagsModal}
          onClose={() => setShowManageTagsModal(false)}
          tags={userState.user.config.tags || []}
          onSave={(newTags: string[]) => {
            dispatch(updateTagsRequest(newTags));
            setShowManageTagsModal(false);
          }}
        />
      </Grid>
    </>
  );
}
